import React, { useEffect, useState } from 'react';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BreadCrumbs from '../Common/BreadCrumbs';
import PageTitleWithToolTip from '../Common/PageTitleWithTooltip';
import { NoData } from '../Common/DataGrid/utils';
import NoDataTableIcon from '../../Icons/empty_table_placeholder.svg';
import useBreadCrumbs from '../../hooks/useBreadCrumbs';
import useNavigate from '../../hooks/useNavigate';
import Routes from '../../routes';
import RegulationColumns from './RegulationColumns';
import NotMeasuredStrip from './NotMeasuredStrip';
import PageFilters from './PageFilters';
import { useComplianceFilter } from './ComplianceFilterContext';
import ConsolidationTables from './ConsolidationTables';
import CalcGuideDialog from './CalcGuideDialog';
import { REGULATIONS, UNMEASURED_REGULATIONS, LEGEND_GRADIENT } from './regulationConfig';
import { CHART_TABLE_LINE, CHART_CALC_GUIDE } from './chartCopy';

// Redesigned Compliance Center landing page (AB#17116), rendered at the existing
// /ComplianceCenter route when the ComplianceCenter feature flag is on (the flag
// is resolved once in ComplianceCenterRoot, not here). A bar opens its regulation
// drill-down once that regulation has one (AB#17128 AC14: EPA 608 first).
// AB#17146 adds the page-level System Manager / Region-Division filter and the
// three consolidation tables below the chart, inside the same flag.
const legendSwatchSx = {
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    position: 'relative',
    overflow: 'hidden',
    background: LEGEND_GRADIENT,
};

const ComplianceCenterRedesign = ({ history }) => {
    useNavigate({
        pageName: 'ComplianceCenter',
    });
    useBreadCrumbs({
        path: { id: 'ComplianceCenter', text: 'Compliance Center', link: history.location.pathname, reset: true },
    });

    const [summary, setSummary] = useState(null);
    const [calcGuideOpen, setCalcGuideOpen] = useState(false);
    const { pageFilter, setPageFilter } = useComplianceFilter();

    useEffect(() => {
        // AB#17128: a slower reply for an earlier filter must never overwrite the current one
        const controller = new AbortController();
        const getData = async () => {
            try {
                const params = new URLSearchParams();
                if (pageFilter.systemManager) params.set('systemManager', pageFilter.systemManager);
                if (pageFilter.regionDivision) params.set('regionDivision', pageFilter.regionDivision);
                const query = params.toString();
                const response = await fetch(Routes.RegulatedAsset.GetComplianceCenterSummary(query ? `?${query}` : ''), {
                    method: 'GET',
                    headers: { Accept: 'application/json' },
                    signal: controller.signal,
                });
                if (response.ok) {
                    setSummary(await response.json());
                }
            } catch (error) {
                // Aborted, or cancelled by leaving the page: not an error worth reporting (N6)
                if (!controller.signal.aborted) console.warn('Compliance Center summary did not load', error);
            }
        };
        getData();
        return () => controller.abort();
    }, [pageFilter]);

    const counts = (list) => Object.fromEntries((list || []).map((r) => [r.key, r]));
    const regulationCounts = counts(summary?.regulations);
    const unmeasuredCounts = counts(summary?.unmeasured);

    // A regulation the instance has no in-scope assets for is hidden entirely (AC3).
    const barRegulations = REGULATIONS
        .map((reg) => ({ ...reg, ...regulationCounts[reg.key] }))
        .filter((reg) => (reg.assetCount ?? 0) > 0);
    const unmeasuredRegulations = UNMEASURED_REGULATIONS
        .map((reg) => ({ ...reg, ...unmeasuredCounts[reg.key] }))
        .filter((reg) => (reg.assetCount ?? 0) > 0);

    // AC 9 (17146): the page-level empty state is about the instance, not the current
    // page filter — a filter that narrows the chart to nothing must not swap the whole
    // page for the empty state (the tabs' own filtered-state copy handles that).
    const pageFilterActive = Boolean(pageFilter.systemManager || pageFilter.regionDivision);
    const isEmpty = summary && !pageFilterActive
        && barRegulations.length === 0 && unmeasuredRegulations.length === 0;

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: 'Compliance Center' },
                ]}
            />
            <Grid
                container
                direction="row"
                alignItems="center"
            >
                <Grid item xs={12}>
                    <div className="complianceCenterDemo">
                        <PageTitleWithToolTip
                            pageTitle="Compliance Center"
                            toolTipText="Use the compliance center to stay up to date on the impact from your maintenance work and to assist in compliance management."
                        />
                    </div>
                </Grid>
                <Grid item xs={12}>
                    {summary && !isEmpty && (
                        <PageFilters value={pageFilter} onChange={setPageFilter} />
                    )}
                    {/* AC8 chart copy: table line under the title, full approved calc
                        guide (T0) in a modal opened by a single click on the info icon —
                        no hover tooltip (product direction, 17116 round 2). */}
                    <Typography variant="h5" style={{ fontSize: '1.125rem' }}>
                        Assets by Applicable Regulation
                        <IconButton
                            onClick={() => setCalcGuideOpen(true)}
                            aria-label="How this chart is calculated"
                            sx={{ ':focus': { outline: 'none' } }}
                        >
                            <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 1 }}>
                        {CHART_TABLE_LINE}
                    </Typography>
                    <CalcGuideDialog
                        open={calcGuideOpen}
                        onClose={() => setCalcGuideOpen(false)}
                        title="How this chart is calculated"
                        sections={CHART_CALC_GUIDE}
                    />
                    <Box
                        sx={{
                            backgroundColor: '#FFFFFF',
                            mb: 2,
                            borderRadius: '8px',
                            border: '1px solid #EBEDF8',
                            p: '16px',
                        }}
                    >
                        {!summary && null}
                        {isEmpty && (
                            // AC6 empty state. Placeholder copy — final product copy pending.
                            <NoData
                                noTableDataIcon={NoDataTableIcon}
                                noResultText="No assets are in scope of a supported regulation yet."
                                noResultText2="Regulation coverage appears here once assets with regulated characteristics are added."
                            />
                        )}
                        {summary && !isEmpty && (
                            <>
                                <Box sx={{ display: 'flex', gap: '16px', justifyContent: 'flex-end', mb: '4px' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'text.secondary' }}>
                                        <Box sx={legendSwatchSx} />
                                        Compliant
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'text.secondary' }}>
                                        <Box sx={legendSwatchSx}>
                                            <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255, 255, 255, 0.78)' }} />
                                        </Box>
                                        Needs attention
                                    </Box>
                                </Box>
                                {barRegulations.length > 0 && (
                                    <RegulationColumns
                                        regulations={barRegulations}
                                        onSelect={(reg) => history.push(`/ComplianceCenter/${reg.key}`)}
                                    />
                                )}
                                {unmeasuredRegulations.length > 0 && (
                                    <NotMeasuredStrip regulations={unmeasuredRegulations} />
                                )}
                            </>
                        )}
                    </Box>
                    {/* AB#17146: the three consolidation tables, below the chart. Not
                        rendered on top of the page-level empty state (AC 9). */}
                    {summary && !isEmpty && (
                        <ConsolidationTables pageFilter={pageFilter} />
                    )}
                </Grid>
            </Grid>
        </>
    );
};

export default ComplianceCenterRedesign;
